import { FC, ReactNode } from 'react';

import { Box, Container, ContainerProps, rem } from '@bonusly/design-system';

export const PAGE_MAX_WIDTH = rem(1024);

export interface ContentContainerProps extends ContainerProps {
	children: ReactNode;
	/**
	 * When true, the container fills the viewport height and children can
	 * use flex: 1 to fill available space. Useful for layouts where content
	 * should fill the screen without causing scroll.
	 *
	 * Added for 1:1 meeting routes so the MeetingContextDrawer can take up
	 * the full available container height without complex height calculations.
	 * Children use flex: 1 to naturally fill the space.
	 */
	fillHeight?: boolean;
	/**
	 * Height to subtract from 100svh when fillHeight is enabled.
	 * Use this to account for fixed elements like TabBar that are outside
	 * the ContentContainer.
	 *
	 * Example: Team Management has a TabBar above the ContentContainer,
	 * so we pass offsetHeight={TAB_BAR_HEIGHT} to subtract it from the
	 * viewport height calculation.
	 */
	offsetHeight?: number | string;
	/**
	 * Optional override for top padding. Uses Chakra spacing scale.
	 *
	 * Added for meeting detail pages that render a "Back to 1:1s" link.
	 * The default top padding is generous, but when a back button is present,
	 * we reduce it to avoid excessive whitespace pushing content down.
	 */
	paddingTop?: number | string;
}

export const ContentContainer: FC<ContentContainerProps> = ({ children, fillHeight, offsetHeight, paddingTop, ...props }) => {
	// Calculate height: 100svh minus any offset (e.g., TabBar height)
	const containerHeight = fillHeight
		? offsetHeight
			? `calc(100svh - ${typeof offsetHeight === 'number' ? rem(offsetHeight) : offsetHeight})`
			: '100svh'
		: undefined;

	// Use explicit padding directions so paddingTop can override without conflicts
	const basePadding = rem(16);
	const smPadding = rem(48);

	return (
		<Box
			pt={paddingTop !== undefined ? paddingTop : { base: basePadding, sm: smPadding }}
			pb={{ base: basePadding, sm: smPadding }}
			px={{ base: basePadding, sm: smPadding }}
			{...(fillHeight && {
				height: containerHeight,
				maxHeight: containerHeight,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			})}
		>
			<Container
				maxW={PAGE_MAX_WIDTH}
				mx="auto"
				p={0}
				{...(fillHeight && {
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					overflow: 'hidden',
				})}
				{...props}
			>
				{children}
			</Container>
		</Box>
	);
};
